
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { TimeSlotForm } from "@/components/admin/TimeSlotForm";
import { TimeSlotGrid } from "@/components/admin/TimeSlotGrid";
import { useToast } from "@/hooks/use-toast";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, CreditCard } from "lucide-react";
import { PaymentStatusView } from "@/components/admin/PaymentStatusView";

const TimeSlots = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [activeView, setActiveView] = useState<'timeSlots' | 'payments'>('timeSlots');

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Authentication Required",
          description: "Please sign in to access the admin area",
          variant: "destructive",
        });
        navigate("/auth");
      }
      setIsLoading(false);
    };

    checkAuth();

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        navigate("/auth");
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, toast]);

  // Fetch time slots with real-time updates
  const { data: timeSlots = [], refetch } = useQuery({
    queryKey: ['timeSlotsCreated'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('time_slots_created')
        .select('*')
        .order('date', { ascending: true })
        .order('start_time', { ascending: true });
      
      if (error) {
        console.error('Error fetching time slots:', error);
        toast({
          title: "Error",
          description: "Failed to fetch time slots",
          variant: "destructive",
        });
        return [];
      }

      return data.map(slot => ({
        ...slot,
        date: new Date(slot.date),
        created_at: new Date(slot.created_at)
      }));
    },
    enabled: !isLoading,
    refetchInterval: 5000, // Refresh every 5 seconds
  });

  // Subscribe to relevant table changes
  useEffect(() => {
    const channel = supabase
      .channel('admin_changes')
      .on(
        'postgres_changes',
        { 
          event: '*',
          schema: 'public',
          table: 'time_slots_created'
        },
        () => {
          console.log('Time slots changed, refreshing...');
          refetch();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bookings'
        },
        () => {
          console.log('Bookings changed, refreshing...');
          refetch();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'payment_status'
        },
        () => {
          console.log('Payment status changed, refreshing...');
          refetch();
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [refetch]);

  if (isLoading) {
    return <div className="container mx-auto py-8">Loading...</div>;
  }

  return (
    <div className="container mx-auto py-8 animate-fadeIn">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">
          {activeView === 'timeSlots' ? 'Time Slots' : 'Payment Status'}
        </h1>
        <div className="flex gap-4">
          <button
            onClick={() => setActiveView('timeSlots')}
            className={`p-2 rounded-full transition-colors ${
              activeView === 'timeSlots'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Time Slots"
          >
            <Clock className="w-5 h-5" />
          </button>
          <button
            onClick={() => setActiveView('payments')}
            className={`p-2 rounded-full transition-colors ${
              activeView === 'payments'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Payment Status"
          >
            <CreditCard className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      {activeView === 'timeSlots' ? (
        <div className="grid md:grid-cols-2 gap-8">
          <TimeSlotForm />
          <TimeSlotGrid timeSlots={timeSlots} />
        </div>
      ) : (
        <PaymentStatusView />
      )}
    </div>
  );
};

export default TimeSlots;
